import { useRecoilValue } from 'recoil';
import { Container } from '../../../../GlobalComponents/Container';
import { Flex } from '../../../../GlobalComponents/Flex';
import { QueryResult } from '../../../../GlobalComponents/QueryResult';
import { Title } from '../../../../GlobalComponents/Title';
import {
	GetUnavailableProductsQuery,
	useGetUnavailableProductsLazyQuery
} from '../../../../graphql';
import { useGetFavoriteProductsQuery } from '../../../graphql';
import { FavoriteProduct } from '../components/FavoriteProduct';
import { isStoreGlobalState, storeIdState } from '../../../../Store/atoms';
import { useAppDispatch, useAppSelector } from '../../../../reduxstore/hooks';
import {
	setStoreSelection,
	setUnavailableProductIds
} from '../../../../domains/UI/store/slice';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const FavoriteProducts = ({ products }: FavoriteProductProps): JSX.Element => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const storeId = useRecoilValue(storeIdState);

	const isStoreGlobal = useRecoilValue(isStoreGlobalState);

	const productIds = products.map((product: FavoriteProduct) =>
		Number(product.product)
	);

	const unavailableProductIds = useAppSelector(
		state => state.uiReducer.unavailableProductIds
	);

	const { loading, data, error } = useGetFavoriteProductsQuery({
		variables: { id: productIds }
	});

	const [getUnavailableProduct, { loading: unavailableProductsLoading }] =
		useGetUnavailableProductsLazyQuery({
			fetchPolicy: 'network-only',
			onCompleted: (data: GetUnavailableProductsQuery) => {
				dispatch(
					setUnavailableProductIds(
						data.unavailableProducts.map(
							product => product.productId
						)
					)
				);
			}
		});

	useEffect(() => {
		if (!isStoreGlobal) getUnavailableProduct({ variables: { storeId } });
	}, [getUnavailableProduct, storeId, isStoreGlobal]);

	const storeSelection = useCallback(() => {
		if (isStoreGlobal)
			dispatch(
				setStoreSelection({
					isOpen: true,
					forSettingOLOStore: true,
					source: 'FAVORITE_PRODUCTS'
				})
			);
	}, [dispatch, isStoreGlobal]);

	useEffect(() => {
		if (isStoreGlobal) storeSelection();
	}, [isStoreGlobal, storeSelection]);

	const isLoading = loading || unavailableProductsLoading;

	const onClick = (id: string, isAvailable: boolean) => {
		if (isStoreGlobal) storeSelection();
		else if (isAvailable) navigate(`/menu/product/${id}`);		
	};

	return (
		<Container>
			<Title>Favorite Products</Title>
			<QueryResult loading={isLoading} data={data} error={error}>
				<Flex $flexDirection="column" $rowGap="24px">
					{data?.favoriteProducts.map(product => (
						<FavoriteProduct
							key={product?.id}
							id={product?.id}
							name={product?.name}
							icon={product?.icon}
							isAvailable={
								isStoreGlobal
									? false
									: !unavailableProductIds.includes(
											Number(product.id)
										)
							}
							isStoreGlobal={isStoreGlobal}
							onClick={onClick}
						/>
					))}
				</Flex>
			</QueryResult>
		</Container>
	);
};

export default FavoriteProducts;

export type FavoriteProductProps = {
	products: FavoriteProduct[];
};
